import { logError } from '@appscience/utils'
import { createAtom } from '@reatom/core'
import { createPrimitiveAtom } from '@reatom/core/primitives'
import { OrderFile, ProposalFilesService } from '../../../../../services'
import { getFilenameFromS3Url } from './order-files.utils'
import { notification } from 'antd'

export const orderFilesLoadingAtom = createPrimitiveAtom<boolean>(false)

type OrderFilesAtomAtomState = {
  abortController: AbortController|null;
  attachedFilesMap: Map<string, OrderFile>;
}

function getInitialState(): OrderFilesAtomAtomState {
  return {
    abortController: null,
    attachedFilesMap: new Map<string, OrderFile>(),
  }
}

// @ts-ignore
export const orderFilesAtom = createAtom(
  {
    init: (urls: Array<string>) => urls,
    setAbortController: (value: AbortController|null) => value,
    cancelRequests: () => {
    },
    reset: () => {
    },
    setAttachedFilesMap: (newAttachedFilesMap: Map<string, OrderFile>) => newAttachedFilesMap,
    uploadFiles: (files: Array<File>) => files,
    deleteFile: (fileName: string) => fileName,
  },
  ({ onAction, schedule }, state = getInitialState()) => {
    onAction('init', (urls: Array<string>) => {
      const attachedFilesMap = urls
        ?.reduce((result, s3Url) => {
          const filename = getFilenameFromS3Url(s3Url)
          return result.set(
            filename,
            {
              filename,
              data: null,
              s3Url,
            },
          )
        },
        new Map<string, OrderFile>(),
        )

      schedule(dispatch => {
        dispatch(orderFilesAtom.setAttachedFilesMap(attachedFilesMap))
      })
    })

    onAction('setAbortController', abortController => {
      state = {
        ...state,
        abortController,
      }
    })

    onAction('cancelRequests', () => {
      state.abortController?.abort?.()
    })

    onAction('reset', () => {
      state = getInitialState()
    })

    onAction('setAttachedFilesMap', newAttachedFilesMap => {
      state = {
        ...state,
        attachedFilesMap: newAttachedFilesMap,
      }
    })

    onAction('uploadFiles', (files: Array<File>) => {
      schedule(async dispatch => {
        dispatch(orderFilesAtom.cancelRequests())
        const abortController = new AbortController()
        const abortSignal = abortController.signal

        dispatch([
          orderFilesLoadingAtom.set(true),
          orderFilesAtom.setAbortController(abortController),
        ])

        try {
          const orderFiles = await ProposalFilesService.uploadOrderFilesToS3({ files, abortSignal }).files

          const prevAttachedFilesMap = new Map<string, OrderFile>(state.attachedFilesMap)
          const attachedFilesMap = orderFiles
            .reduce((result, orderFile) => {
              result.set(orderFile.filename, orderFile)
              return result
            },
            prevAttachedFilesMap,
            )
          dispatch(orderFilesAtom.setAttachedFilesMap(attachedFilesMap))
        }
        catch (error) {
          notification.error({
            message: 'Files failed to upload',
            duration: 5,
          })
          logError(`Files failed to upload: ${(error as Error)?.message}`)

          dispatch(orderFilesAtom.cancelRequests())
        }
        finally {
          dispatch(orderFilesLoadingAtom.set(false))
        }
      })
    })

    onAction('deleteFile', (fileName: string) => {
      if (!fileName) {
        return
      }

      schedule(dispatch => {
        const prevAttachedFilesMap = new Map<string, OrderFile>(state.attachedFilesMap)
        prevAttachedFilesMap.delete(fileName)

        dispatch(orderFilesAtom.setAttachedFilesMap(prevAttachedFilesMap))
      })
    })

    return state
  },
)
